<template>
  <div>
    <div v-if="mediaFile" class="eraser-container">
      <img ref="imageEl" class="eraser-image" :src="mediaFile" alt="" @load="onImageLoad" />
      <canvas
        v-show="overlayVisible"
        ref="canvasEl"
        class="eraser-overlay"
        :class="{ 'completed-state': isCompleted }"
        @mousedown="onPointerDown"
        @mousemove="onPointerMove"
        @mouseup="onPointerUp"
        @mouseleave="onPointerUp"
        @touchstart.prevent="onTouchStart"
        @touchmove.prevent="onTouchMove"
        @touchend.prevent="onPointerUp"
      />
    </div>
  </div>
  
</template>

<script>
export default {
  name: 'GameImageEraser',
  props: {
    block: {
      type: Object,
      required: true
    },
    // Overlay fill color
    overlayColor: {
      type: String,
      default: 'rgba(192,192,192,0.95)'
    },
    isCompleted: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      mediaFile: null,
      overlayVisible: true,
      isErasing: false,
      erasedPercent: 0,
      completionThreshold: 0.6,
      lastX: 0,
      lastY: 0,
      measureQueued: false
    }
  },
  watch: {
    block: {
      handler(newBlock) {
        if (newBlock?.data?.media) {
          this.mediaFile = newBlock.data.media.preview_url
        }

        if (newBlock.data?.percentage_complete) {
          this.completionThreshold = parseInt(newBlock.data.percentage_complete) / 100;
        }
        // Reset state when block changes (unless already completed)
        if (!this.isCompleted) {
          this.overlayVisible = true
          this.erasedPercent = 0
          this.$nextTick(() => this.initializeCanvas())
        } else {
          // If completed, hide overlay immediately
          this.overlayVisible = false
          this.erasedPercent = 1
        }
      },
      immediate: true
    },
    isCompleted: {
      handler(newValue) {
        if (newValue) {
          // When marked as completed, hide overlay and set erased to 100%
          this.overlayVisible = false
          this.erasedPercent = 1
        } else {
          // When not completed, reset state
          this.overlayVisible = true
          this.erasedPercent = 0
          this.$nextTick(() => this.initializeCanvas())
        }
      },
      immediate: true
    }
  },
  methods: {
    onImageLoad() {
      // Don't initialize canvas if already completed
      if (!this.isCompleted) {
        this.initializeCanvas()
      }
    },
    initializeCanvas() {
      // Don't initialize if already completed
      if (this.isCompleted) {
        return
      }
      
      const img = this.$refs.imageEl
      const canvas = this.$refs.canvasEl
      if (!img || !canvas) return
      // Match canvas display size to image rendered size
      const rect = img.getBoundingClientRect()
      canvas.width = Math.max(1, Math.floor(rect.width))
      canvas.height = Math.max(1, Math.floor(rect.height))
      canvas.style.width = rect.width + 'px'
      canvas.style.height = rect.height + 'px'

      const ctx = canvas.getContext('2d')
      ctx.globalCompositeOperation = 'source-over'
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.fillStyle = this.overlayColor
      ctx.fillRect(0, 0, canvas.width, canvas.height)
      this.erasedPercent = 0
      this.overlayVisible = true
      console.log("initializeCanvas");
      console.log(this.block.data);
      if (this.block.data?.percentage_complete) {
        this.completionThreshold = parseInt(this.block.data.percentage_complete) / 100;
      }
    },
    getEraserRadius() {
      const canvas = this.$refs.canvasEl
      if (!canvas) return 0
      return Math.max(4, Math.floor(canvas.width * 0.05))
    },
    canvasPointFromEvent(clientX, clientY) {
      const canvas = this.$refs.canvasEl
      if (!canvas) return { x: 0, y: 0 }
      const rect = canvas.getBoundingClientRect()
      return { x: clientX - rect.left, y: clientY - rect.top }
    },
    eraseAt(x, y) {
      const canvas = this.$refs.canvasEl
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const r = this.getEraserRadius()
      ctx.save()
      ctx.globalCompositeOperation = 'destination-out'
      ctx.lineCap = 'round'
      ctx.lineJoin = 'round'
      if (this.lastX === null) {
        ctx.beginPath()
        ctx.arc(x, y, r, 0, Math.PI * 2)
        ctx.fill()
      } else {
        ctx.beginPath()
        ctx.moveTo(this.lastX, this.lastY)
        ctx.lineWidth = r * 2
        ctx.lineTo(x, y)
        ctx.stroke()
      }
      ctx.restore()
      this.lastX = x
      this.lastY = y
      this.queueMeasure()
    },
    onPointerDown(e) {
      // Don't allow interaction if already completed
      if (this.isCompleted || !this.overlayVisible) return
      this.isErasing = true
      const { x, y } = this.canvasPointFromEvent(e.clientX, e.clientY)
      this.lastX = null
      this.lastY = null
      this.eraseAt(x, y)
    },
    onPointerMove(e) {
      // Don't allow interaction if already completed
      if (this.isCompleted || !this.isErasing || !this.overlayVisible) return
      const { x, y } = this.canvasPointFromEvent(e.clientX, e.clientY)
      this.eraseAt(x, y)
    },
    onPointerUp() {
      this.isErasing = false
      this.lastX = null
      this.lastY = null
      this.measureErased()
    },
    onTouchStart(e) {
      // Don't allow interaction if already completed
      if (this.isCompleted || !this.overlayVisible) return
      this.isErasing = true
      const t = e.touches[0]
      const { x, y } = this.canvasPointFromEvent(t.clientX, t.clientY)
      this.lastX = null
      this.lastY = null
      this.eraseAt(x, y)
    },
    onTouchMove(e) {
      // Don't allow interaction if already completed
      if (this.isCompleted || !this.isErasing || !this.overlayVisible) return
      const t = e.touches[0]
      const { x, y } = this.canvasPointFromEvent(t.clientX, t.clientY)
      this.eraseAt(x, y)
    },
    queueMeasure() {
      if (this.measureQueued) return
      this.measureQueued = true
      requestAnimationFrame(() => {
        this.measureQueued = false
        this.measureErased()
      })
    },
    measureErased() {
      const canvas = this.$refs.canvasEl
      if (!canvas) return
      const ctx = canvas.getContext('2d')
      const { width, height } = canvas
      try {
        const imageData = ctx.getImageData(0, 0, width, height)
        const data = imageData.data
        let transparentCount = 0
        // Sample every 4th pixel to reduce cost
        const step = 4 * 4
        for (let i = 3; i < data.length; i += step) {
          if (data[i] === 0) transparentCount++
        }
        const totalSampled = Math.floor((data.length / 4) / 4)
        const percent = totalSampled > 0 ? transparentCount / totalSampled : 0
        this.erasedPercent = percent
        if (percent >= this.completionThreshold) {
          this.overlayVisible = false
          this.$emit('complete')
        }
      } catch (err) {
        // getImageData may fail if canvas is tainted, ignore measurement
      }
    }
  }
}
</script>

<style scoped>
.eraser-container {
  position: relative;
  display: inline-block;
}

.eraser-image {
  display: block;
  max-width: 100%;
  height: auto;
}

.eraser-overlay {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  touch-action: none;
  cursor: crosshair;
}

.eraser-overlay.completed-state {
  cursor: default;
  pointer-events: none;
}

</style>